// @ts-check

/**
 * Animated details element.
 * Requires a transition on element following summary to listen for transitionend
 */
class AnimatedDetails extends HTMLElement {
  constructor() {
    super();
    this.setup();
  }

  setup() {
    this.detailsEl = this.querySelector("details");
    this.summary = this.querySelector("summary");
    this.toggledEl = this.summary.nextElementSibling;
    this.header = document.querySelector("#header");
    this.headerContent = document.querySelector(".header-content");
    this.screenSizeCheck();
    window.addEventListener("resize", this.screenSizeCheck.bind(this));
    this.theme = document
      .querySelector("sticky-header")
      .getAttribute("data-header-default-theme");
    this.body = document.body;

    this.openState = "open";
    this.closedState = "closed";
    this.openingState = "opening";
    this.closingState = "closing";

    this.classes = {
      open: "open",
      closed: "closed",
      beforeClosing: "before-closing",
      beforeOpening: "before-opening",
      closing: "closing",
      opening: "opening",
      theme: "theme-neutral",
      transparent: "theme-transparent",
    };

    this.state = this.detailsEl.hasAttribute("open")
      ? this.openState
      : this.closedState;

    this.detailsEl.classList.add(this.classes[this.state]);
    // ensures doesn't close if clicked inside
    this.toggledEl.tabIndex = -1;

    /**
     * when a transition has ended, if this property was what ended then
     * the details is considered "closed" or "open"
     */
    this.transitionedPropertyName = "transform";

    // Hover functionality
    this.hoverTimeout = null;
    this.isHoverEnabled = true;

    this.bindListeners();
  }

  bindListeners() {
    this.detailsEl.addEventListener("toggle", this.onToggle.bind(this));
    this.summary.addEventListener("click", this.onClick.bind(this));
    this.detailsEl.addEventListener("focusout", this.onFocusOut.bind(this));
    this.toggledEl.addEventListener(
      "transitionend",
      this.onTransitionEnd.bind(this)
    );
    this.body?.addEventListener("click", this.onBodyClick.bind(this));

    // Add hover listeners
    this.addEventListener("mouseenter", this.onMouseEnter.bind(this));
    this.addEventListener("mouseleave", this.onMouseLeave.bind(this));

    // Add escape key listener
    document.addEventListener("keydown", this.onKeyDown.bind(this));
  }

  // Check if any animated-details are open
  updateGlobalState() {
    const allAnimatedDetails = document.querySelectorAll("animated-details");
    const anyOpen = Array.from(allAnimatedDetails).some((detail) => {
      const detailsEl = detail.querySelector("details");
      return detailsEl && detailsEl.hasAttribute("open");
    });

    if (anyOpen) {
      this.headerContent?.classList.add("open");
      this.body.classList.add("overlay");
    } else {
      this.headerContent?.classList.remove("open");
      this.body.classList.remove("overlay");
    }
  }

  onMouseEnter() {
    if (!this.isHoverEnabled) return;

    clearTimeout(this.hoverTimeout);
    if (this.state === this.closedState || this.state === this.closingState) {
      this.open();
    }
  }

  onMouseLeave() {
    if (!this.isHoverEnabled) return;

    this.hoverTimeout = setTimeout(() => {
      if (this.state === this.openState || this.state === this.openingState) {
        this.close();
      }
    }, 100); // Small delay to prevent flickering
  }

  onKeyDown(e) {
    if (
      e.key === "Escape" &&
      (this.state === this.openState || this.state === this.openingState)
    ) {
      this.close();
    }
  }

  // onBodyClick handles clicks outside the details element and the body.overlay click. If clicked, hide the details element
  onBodyClick(e) {
    if (
      this.state === this.openState &&
      !document.body.classList.contains("rebuy-cart-visible") &&
      !this.contains(e.target)
    ) {
      this.close();
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        !this.contains(document.activeElement) &&
        this.state === this.openState
      ) {
        this.close();
      }
      /**
       * slight delay seems to resolve issues with click
       * not registering when it also triggers a focusout event on FF.
       */
    }, 100);
  }

  screenSizeCheck() {
    this.screenWidth = window.innerWidth;

    if (this.screenWidth < 1050) {
      this.isHoverEnabled = false;
      this.close();
      this.updateGlobalState();
    } else {
      this.isHoverEnabled = true;
    }
  }

  onTransitionEnd(e) {
    if (
      e.propertyName === this.transitionedPropertyName &&
      e.target === this.toggledEl
    ) {
      if (this.state === this.closingState) {
        this.cancelOpening();
        this.cancelClosing();
        this.detailsEl.classList.add(this.classes.closed);
        this.detailsEl.removeAttribute("open");
        this.state = this.closedState;
        this.updateGlobalState();
      } else {
        this.cancelClosing();
        this.cancelOpening();
        this.detailsEl.classList.add(this.classes.open);
        this.state = this.openState;
        this.updateGlobalState();
      }
    }
  }

  cancelOpening() {
    this.detailsEl.classList.remove(this.classes.opening);
    this.detailsEl.classList.remove(this.classes.beforeOpening);
    this.detailsEl.classList.remove(this.classes.open);
  }

  cancelClosing() {
    this.detailsEl.classList.remove(this.classes.closing);
    this.detailsEl.classList.remove(this.classes.beforeClosing);
    this.detailsEl.classList.remove(this.classes.closed);
  }

  onClick(e) {
    e.preventDefault();

    // Disable hover temporarily when clicking to prevent conflicts
    this.isHoverEnabled = false;
    clearTimeout(this.hoverTimeout);

    // Re-enable hover after a short delay
    setTimeout(() => {
      this.isHoverEnabled = true;
    }, 500);

    if (
      (this.state === this.openState && e.target) ||
      (this.state == this.openingState && e.target)
    ) {
      this.close();
    } else {
      this.open();
    }
  }

  close() {
    if (this.state !== this.closedState && this.state !== this.closingState) {
      this.state = this.closingState;

      requestAnimationFrame(() => {
        this.cancelOpening();
        this.detailsEl.classList.add(this.classes.beforeClosing);
        requestAnimationFrame(() => {
          this.detailsEl.classList.add(this.classes.closing);
        });
      });
    }
  }

  open() {
    if (this.state !== this.openingState && this.state !== this.openState) {
      const wasClosing = this.state === this.closingState;
      this.state = this.openingState;
      this.detailsEl.setAttribute("open", "true");
      if (wasClosing) {
        this.cancelClosing();
        this.detailsEl.classList.add(this.classes.opening);
      } else {
        requestAnimationFrame(() => {
          this.cancelClosing();
          this.detailsEl.classList.add(this.classes.beforeOpening);
          requestAnimationFrame(() => {
            this.detailsEl.classList.add(this.classes.opening);
          });
        });
      }
    }
  }

  onToggle() {
    if (this.detailsEl.hasAttribute("open")) {
      this.summary.setAttribute("aria-expanded", "true");
      this.updateGlobalState();
    } else {
      this.summary.setAttribute("aria-expanded", "false");
      this.updateGlobalState();
    }
  }
}

customElements.define("animated-details", AnimatedDetails);
